import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReordenItem } from "./reorden.types";

interface Props {
  items: ReordenItem[];
  autoPlayMs?: number;
}

/**
 * Zona B — Preview del carrusel. Reproduce en vivo como se veria el carrusel con
 * el orden actual (autoplay + navegacion manual). Se re-renderiza automaticamente
 * cuando cambia `items` (al reordenar en la Zona A).
 *
 * Solo visual: no consume ni guarda nada.
 */
const CarruselPreview: React.FC<Props> = ({ items, autoPlayMs = 2500 }) => {
  const [index, setIndex] = useState(0);

  // Clave estable que cambia cuando cambia el orden, para reiniciar el ciclo.
  const ordenKey = useMemo(
    () => items.map((i) => i.idDistribucion).join("-"),
    [items]
  );

  // Si el orden cambia y el indice queda fuera de rango, lo acotamos.
  useEffect(() => {
    setIndex((prev) => (items.length === 0 ? 0 : prev % items.length));
  }, [ordenKey, items.length]);

  // Autoplay.
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, autoPlayMs);
    return () => clearInterval(t);
  }, [ordenKey, items.length, autoPlayMs]);

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Nada que previsualizar.
      </Typography>
    );
  }

  const current = items[Math.min(index, items.length - 1)];
  const go = (dir: number) =>
    setIndex((prev) => (prev + dir + items.length) % items.length);

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "grey.900",
          height: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="carrusel-preview"
      >
        {current.urlBanner ? (
          <img
            src={current.urlBanner}
            alt={current.titulo}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <Typography color="grey.400">{current.titulo}</Typography>
        )}

        <IconButton
          aria-label="anterior"
          onClick={() => go(-1)}
          sx={{
            position: "absolute",
            left: 8,
            bgcolor: "rgba(255,255,255,0.7)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label="siguiente"
          onClick={() => go(1)}
          sx={{
            position: "absolute",
            right: 8,
            bgcolor: "rgba(255,255,255,0.7)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {/* Indicadores (dots) en el orden actual */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mt: 1,
        }}
      >
        {items.map((it, i) => (
          <Box
            key={it.idDistribucion}
            onClick={() => setIndex(i)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              cursor: "pointer",
              bgcolor: i === index ? "primary.main" : "grey.400",
            }}
          />
        ))}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", mt: 0.5 }}
      >
        {index + 1} / {items.length} — {current.titulo}
      </Typography>
    </Box>
  );
};

export default CarruselPreview;
